import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  UseInfiniteQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { fetchFieldVersions, ICallApi, IFieldVersion, revertFieldVersion } from '_/api/history/fieldVersions.api';
import { notification } from 'antd';

export const FIELD_VERSIONS_QUERY_KEY = (
  objectName: string,
  resourceId: string | undefined,
  childPath: string | undefined,
  fieldPath: string,
): unknown[] => ['fieldVersions', objectName, resourceId || '', childPath || '', fieldPath];

// Generic over "which resource" -- pass whichever zeusSdk.<resource> owns resourceId
// (zeusSdk.collections, zeusSdk.flows, zeusSdk.flowSegments, ...) and, for a linked
// sub-resource such as a Collection's `settings`, the childPath ('settings'); omit
// childPath for a field that lives directly on the resource's own row.
export const useFieldVersions = (
  api: ICallApi & { objectName?: string },
  resourceId: string | undefined,
  childPath: string | undefined,
  fieldPath: string,
): UseInfiniteQueryResult<InfiniteData<{ items: IFieldVersion[]; cursor?: string }>> => {
  return useInfiniteQuery({
    // api is a stable zeusSdk.<resource> singleton, never reassigned across renders --
    // api.objectName is already part of the key as its identity proxy.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: FIELD_VERSIONS_QUERY_KEY(api.objectName || '', resourceId, childPath, fieldPath),
    queryFn: async ({ pageParam }) => {
      const response = await fetchFieldVersions(api, resourceId!, childPath, fieldPath, pageParam);
      return { items: response.items, cursor: response.cursor };
    },
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!resourceId,
  });
};

export const useRevertFieldVersion = (
  api: ICallApi & { objectName?: string },
  resourceId: string | undefined,
  childPath: string | undefined,
  fieldPath: string,
): UseMutationResult<any, Error, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (value: unknown) => revertFieldVersion(api, resourceId!, childPath, fieldPath, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: FIELD_VERSIONS_QUERY_KEY(api.objectName || '', resourceId, childPath, fieldPath),
      });
      notification.success({ message: 'Field restored to previous value' });
    },
    onError: (error: any) => {
      console.error('Error restoring field version', error);
      notification.error({ message: error.message || 'Error restoring field' });
    },
  });
};
